import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Sparkles,
  FileText,
  Users,
  Banknote,
  Briefcase,
  Activity,
  CreditCard,
  ShieldCheck,
  UserPlus,
  Settings2,
  Calendar,
  PenTool,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { formatMoney } from '../../design-system/format';
import { MarketingPageHead } from '../MarketingPageHead';
import { JsonLd } from '../seo/JsonLd';
import { buildFaqPageSchema } from '../seo/schema';
import { useLivePrices } from '../useLivePrices';
import {
  FALLBACK_PRICES, FALLBACK_SEAT_BASES, FALLBACK_INCLUDED_SEATS,
  computeMonthlyTotal, formatPayrollPriceLabel, type PricedModuleKey,
} from '../pricingMath';

/**
 * PricingPage
 * --------------------------------------------------------------
 * Worqly pricing page. Core is a flat $9/mo platform fee every workspace
 * pays; on top of that, some modules are flat (Invoicing, Projects, E-Sign)
 * and some are metered per employee (Time & Attendance, Monitoring, Payroll)
 * using the exact clock-in / monitored / paid vocabulary. This page is the
 * source of truth users land on when they want to know what they'll
 * actually pay.
 *
 * Sections (canonical SaaS pricing anatomy):
 *   1. Hero — H1 + sub + dual CTA + micro-trust line
 *   2. Live price builder — interactive module + employee-count calculator
 *   3. Module breakdown table — Core + 6 modules, full details
 *   4. Trial mechanics — 3-step explainer
 *   5. Comparison — Worqly vs. stitched-together legacy stack
 *   6. FAQ — 8 pricing-specific questions
 *   7. Footer CTA — email capture → /signup
 *
 * Visual language matches LandingPage.tsx exactly: coral accent,
 * pearl canvas, font-light display headings + italic serif punch,
 * rounded-3xl translucent cards, dual light/dark via dark: variants.
 *
 * PRICING SOURCE — live from GET /api/public/site/prices (the
 * module_prices table, editable in the super-admin panel) via
 * usePricedModules(). The base/seat numbers in MODULES below are
 * FALLBACKS ONLY (see pricingMath.ts's FALLBACK_PRICES, the single source
 * shared with LandingPage.tsx), used until the fetch resolves or if the
 * backend is unreachable — icons/copy/defaultOn always come from here.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

/* ─────────────────────────────────────────────────────────────
 * Pricing data — the locked launch book (migration 058 +
 * api/modules/platform-billing/billing-engine.ts). Core is a flat
 * platform fee that's always on (dashboard, records, roles & access,
 * security, support — plus email log, expenses, and payments included
 * free, no separate charge). Invoicing / Projects / E-Sign are flat.
 * Time & Attendance / Monitoring / Payroll are metered per employee —
 * see SEAT_LABEL in pricingMath.ts for the exact vocabulary. An
 * employee is billed clock-in OR monitored, never both; Payroll stacks
 * independently.
 * ───────────────────────────────────────────────────────────── */

type ModuleId = PricedModuleKey;

type ModuleDef = {
  id: ModuleId;
  name: string;
  base: number;         // monthly base (USD) — for payroll, the banded base fee
  seat: number;          // per-employee rate (USD) — 0 for flat modules
  seatLabel?: string;    // e.g. "clock-in employee" — only for metered modules
  included?: number;     // employees bundled into `base` before `seat` applies (payroll only)
  Icon: React.ComponentType<{ className?: string }>;
  short: string;         // one-line blurb for table
  defaultOn: boolean;    // checked on first render of the builder
  fixed?: boolean;       // can't be turned off (Core always on)
};

const MODULES: ModuleDef[] = [
  {
    id: 'core',
    name: 'Core',
    base: FALLBACK_PRICES.core,
    seat: 0,
    Icon: ShieldCheck,
    short: 'Dashboard, records, roles & access, security, support. Plus email log, expenses, and payments — included, no extra charge.',
    defaultOn: true,
    fixed: true,
  },
  {
    id: 'invoicing',
    name: 'Invoicing',
    base: FALLBACK_PRICES.invoicing,
    seat: 0,
    Icon: FileText,
    short: 'Branded PDFs, Stripe pay links, recurring billing, automatic reminders.',
    defaultOn: true,
  },
  {
    id: 'projects',
    name: 'Projects',
    base: FALLBACK_PRICES.projects,
    seat: 0,
    Icon: Briefcase,
    short: 'Tasks, hours, margin per project. Time tracking included.',
    defaultOn: false,
  },
  {
    id: 'esign',
    name: 'E-Sign',
    base: FALLBACK_PRICES.esign,
    seat: 0,
    Icon: PenTool,
    short: 'Send documents for legally binding electronic signature — official signature number and public verification included.',
    defaultOn: false,
  },
  {
    id: 'hr',
    name: 'Time & Attendance',
    base: 0,
    seat: FALLBACK_PRICES.hr,
    seatLabel: 'clock-in employee',
    Icon: Users,
    short: 'Clock-in, attendance, leave, magic-link onboarding. Billed per clock-in employee.',
    defaultOn: false,
  },
  {
    id: 'monitoring',
    name: 'Monitoring',
    base: 0,
    seat: FALLBACK_PRICES.monitoring,
    seatLabel: 'monitored employee',
    Icon: Activity,
    short: 'Opt-in desktop agent for screenshots and activity tracking. Billed per monitored employee.',
    defaultOn: false,
  },
  {
    id: 'payroll',
    name: 'Payroll',
    base: FALLBACK_SEAT_BASES.payroll,
    seat: FALLBACK_PRICES.payroll,
    seatLabel: 'employee',
    included: FALLBACK_INCLUDED_SEATS.payroll,
    Icon: Banknote,
    short: `Runs that respect attendance and leave. Payslips and bank export. Includes ${FALLBACK_INCLUDED_SEATS.payroll} employees, then billed per employee beyond that.`,
    defaultOn: false,
  },
];

const DEFAULT_EMPLOYEES = 5;
const MIN_EMPLOYEES = 0;
const MAX_EMPLOYEES = 500;

/**
 * MODULES with base/seat overlaid from the live public prices endpoint.
 * Falls back to the hardcoded numbers until (or unless) the fetch resolves.
 */
function usePricedModules(): ModuleDef[] {
  const live = useLivePrices();
  return React.useMemo(() => {
    if (!live) return MODULES;
    return MODULES.map((m) => {
      const lp = live[m.id];
      if (!lp) return m;
      return {
        ...m,
        base: lp.amount_per_month,
        seat: lp.seat_amount_per_month,
        included: m.id === 'payroll' ? (lp.included_seats || m.included) : m.included,
      };
    });
  }, [live]);
}

/** Price display + secondary caption for a module's checkbox row. Payroll
 *  gets the exact banded label ("$15/mo — includes 10 employees, then $1.50
 *  each"); every other module keeps the simple flat/seat display. */
function priceCells(m: ModuleDef): { primary: string; secondary: string } {
  if (m.base === 0 && m.seat === 0) return { primary: 'Free', secondary: '—' };
  if (m.id === 'payroll') {
    return { primary: formatPayrollPriceLabel(m.base, m.seat, m.included ?? 0), secondary: '' };
  }
  if (m.seat > 0) {
    return { primary: `${formatMoney(m.seat, 'USD')}/mo`, secondary: m.seatLabel ? `per ${m.seatLabel}` : '—' };
  }
  return { primary: `${formatMoney(m.base, 'USD')}/mo`, secondary: '—' };
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

export function PricingPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Pricing"
        description="Per-module, per-employee pricing. Pay only for what you use — invoicing, payroll, time & attendance, projects, e-sign, monitoring. 7 days free, no card required."
      />
      <Hero />
      <PriceBuilder />
      <ModuleTable />
      <TrialSteps />
      <Comparison />
      <FAQ />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 1. HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Sparkles className="h-3 w-3 text-coral" />
              7 days free. No card required.
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            Pay only for what{' '}
            <span className="italic font-serif text-coral">your team uses.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Pick modules. Pay per employee only where it counts. One monthly bill. No bundles, no
            surprise upsells, no annual lock-in.
          </motion.p>

          <motion.div
            variants={FADE_UP}
            className="mt-8 flex flex-wrap gap-3 justify-center"
          >
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Start free
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="mailto:hello@worqly.io?subject=Talk%20to%20sales"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
            >
              Talk to sales
            </a>
          </motion.div>

          <motion.p
            variants={FADE_UP}
            className="mt-5 text-[12.5px] text-ink/55 dark:text-white/55 flex flex-wrap items-center gap-x-2 gap-y-1 justify-center"
          >
            <span>No card to start</span>
            <span className="text-ink/25 dark:text-white/25">·</span>
            <span>Add card before day 8</span>
            <span className="text-ink/25 dark:text-white/25">·</span>
            <span>Cancel any time</span>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 2. LIVE PRICE BUILDER
 * ───────────────────────────────────────────────────────────── */

function PriceBuilder() {
  const modules = usePricedModules();
  const byId = React.useMemo(() => {
    const m = new Map<ModuleId, ModuleDef>();
    modules.forEach((mod) => m.set(mod.id, mod));
    return m;
  }, [modules]);

  // Pre-checked modules: Core (always) + Invoicing.
  const initial = React.useMemo(() => {
    const set = new Set<ModuleId>();
    MODULES.forEach((m) => {
      if (m.defaultOn && !m.fixed) set.add(m.id);
    });
    return set;
  }, []);

  const [enabled, setEnabled] = React.useState<Set<ModuleId>>(initial);
  const [employees, setEmployees] = React.useState<number>(DEFAULT_EMPLOYEES);
  const [monitored, setMonitored] = React.useState<number>(0);

  const toggleable = modules.filter((m) => !m.fixed);

  function toggle(id: ModuleId) {
    const mod = byId.get(id);
    if (mod?.fixed) return;
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setEmployeesClamped(v: number) {
    if (!isFinite(v)) return;
    const clamped = Math.max(MIN_EMPLOYEES, Math.min(MAX_EMPLOYEES, Math.round(v)));
    setEmployees(clamped);
    setMonitored((m) => Math.min(m, clamped));
  }

  function setMonitoredClamped(v: number) {
    if (!isFinite(v)) return;
    const clamped = Math.max(0, Math.min(employees, Math.round(v)));
    setMonitored(clamped);
  }

  const corePrice = byId.get('core')?.base ?? FALLBACK_PRICES.core;
  const invoicingPrice = byId.get('invoicing')?.base ?? FALLBACK_PRICES.invoicing;
  const projectsPrice = byId.get('projects')?.base ?? FALLBACK_PRICES.projects;
  const esignPrice = byId.get('esign')?.base ?? FALLBACK_PRICES.esign;
  const hrSeatPrice = byId.get('hr')?.seat ?? FALLBACK_PRICES.hr;
  const monitoringSeatPrice = byId.get('monitoring')?.seat ?? FALLBACK_PRICES.monitoring;
  const payrollBasePrice = byId.get('payroll')?.base ?? FALLBACK_SEAT_BASES.payroll;
  const payrollSeatPrice = byId.get('payroll')?.seat ?? FALLBACK_PRICES.payroll;
  const payrollIncludedSeats = byId.get('payroll')?.included ?? FALLBACK_INCLUDED_SEATS.payroll;

  const invoicingOn = enabled.has('invoicing');
  const projectsOn = enabled.has('projects');
  const esignOn = enabled.has('esign');
  const hrOn = enabled.has('hr');
  const monitoringOn = enabled.has('monitoring');
  const payrollOn = enabled.has('payroll');

  // Totals — pure arithmetic (mirrors the real billing engine), recomputed on
  // every render via the shared computeMonthlyTotal helper.
  const breakdown = computeMonthlyTotal({
    corePrice, invoicingPrice, projectsPrice, esignPrice,
    hrSeatPrice, monitoringSeatPrice,
    payrollBasePrice, payrollSeatPrice, payrollIncluded: payrollIncludedSeats,
    invoicingOn, projectsOn, esignOn, hrOn, monitoringOn, payrollOn,
    employees, monitored,
  });
  const {
    total, flats: flatsTotal, hrBillableCount, hrCost, monitoredCount, monitoringCost,
    payrollBillableCount, payrollCost,
  } = breakdown;

  const activeModules = [byId.get('core')!, ...toggleable.filter((m) => enabled.has(m.id))];

  return (
    <section
      id="builder"
      className="py-16 lg:py-20 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5"
    >
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Price builder</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Build the bill you'd actually pay.
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl"
          >
            Toggle modules, set your employee count — your monthly total updates live. No sign-up
            needed.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={FADE_UP}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6"
        >
          {/* Left — module checkboxes + employee counters */}
          <div className="lg:col-span-7 rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-6 lg:p-8">
            <div className="flex items-center justify-between mb-5">
              <Eyebrow>Modules</Eyebrow>
              <span className="text-[12px] text-ink/55 dark:text-white/55 tabular-nums">
                {activeModules.length} of {modules.length} selected
              </span>
            </div>

            <ul className="space-y-2">
              {modules.map((m) => {
                const checked = m.fixed ? true : enabled.has(m.id);
                const isFixed = !!m.fixed;
                return (
                  <li key={m.id}>
                    <label
                      className={
                        'flex items-center gap-3 p-3 rounded-2xl transition-colors cursor-pointer ' +
                        (isFixed
                          ? 'bg-pearl-50 dark:bg-white/[0.03] cursor-not-allowed opacity-90'
                          : checked
                            ? 'bg-coral/8 ring-1 ring-coral/30 dark:bg-coral/10'
                            : 'hover:bg-pearl-50 dark:hover:bg-white/[0.04] ring-1 ring-transparent')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(m.id)}
                        disabled={isFixed}
                        aria-label={`Toggle ${m.name}`}
                        className="sr-only"
                      />
                      <span
                        aria-hidden
                        className={
                          'inline-flex h-5 w-5 items-center justify-center rounded-md ring-1 transition-colors shrink-0 ' +
                          (checked
                            ? 'bg-coral text-white ring-coral'
                            : 'bg-white dark:bg-white/10 ring-ink/15 dark:ring-white/20')
                        }
                      >
                        {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>

                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shrink-0">
                        <m.Icon className="h-4 w-4" />
                      </span>

                      {/* The badge used to sit INSIDE the truncating span, so on a
                       *  narrow screen it was clipped along with the name. Give the
                       *  row its own flex so only the name truncates. */}
                      <span className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-ink dark:text-white truncate">
                          {m.name}
                        </span>
                        {isFixed && (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-ink/5 dark:bg-white/10 text-[10px] font-bold uppercase tracking-[0.10em] text-ink/55 dark:text-white/55">
                            Always on
                          </span>
                        )}
                      </span>

                      {/* Capped rather than shrink-0: payroll's label ("$15/mo —
                       *  includes 10 employees, then $1.50 each") is long enough to
                       *  push 151px past the card edge on a phone. */}
                      <span className="text-right shrink-0 max-w-[52%] sm:max-w-none">
                        <span className="block text-[13px] font-bold tabular-nums text-ink dark:text-white">
                          {priceCells(m).primary}
                        </span>
                        {priceCells(m).secondary && (
                          <span className="block text-[11.5px] tabular-nums text-coral">
                            {priceCells(m).secondary}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>

            {/* Employee counters */}
            <div className="mt-8 rounded-2xl bg-pearl-50 dark:bg-white/[0.03] p-5 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Eyebrow>Total employees</Eyebrow>
                    <p className="mt-1 text-[12.5px] text-ink/55 dark:text-white/55">
                      Everyone active in your workspace.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEmployeesClamped(employees - 1)}
                      aria-label="Decrease total employees"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-white/10 ring-1 ring-ink/10 dark:ring-white/15 text-ink dark:text-white hover:bg-pearl-100 dark:hover:bg-white/15 transition-colors focus-coral"
                    >
                      <span className="text-[16px] leading-none">−</span>
                    </button>
                    <input
                      type="number"
                      value={employees}
                      min={MIN_EMPLOYEES}
                      max={MAX_EMPLOYEES}
                      onChange={(e) => setEmployeesClamped(Number(e.target.value))}
                      aria-label="Total employees"
                      className="w-16 h-9 text-center rounded-full bg-white dark:bg-white/10 ring-1 ring-ink/10 dark:ring-white/15 text-ink dark:text-white text-[14px] font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-coral/40"
                    />
                    <button
                      type="button"
                      onClick={() => setEmployeesClamped(employees + 1)}
                      aria-label="Increase total employees"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-white/10 ring-1 ring-ink/10 dark:ring-white/15 text-ink dark:text-white hover:bg-pearl-100 dark:hover:bg-white/15 transition-colors focus-coral"
                    >
                      <span className="text-[16px] leading-none">+</span>
                    </button>
                  </div>
                </div>
                <input
                  type="range"
                  min={MIN_EMPLOYEES}
                  max={MAX_EMPLOYEES}
                  value={employees}
                  onChange={(e) => setEmployeesClamped(Number(e.target.value))}
                  aria-label="Total employees slider"
                  className="w-full accent-coral cursor-pointer"
                />
                <div className="mt-1 flex items-center justify-between text-[11px] text-ink/45 dark:text-white/40 tabular-nums">
                  <span>{MIN_EMPLOYEES}</span>
                  <span>{MAX_EMPLOYEES}+</span>
                </div>
              </div>

              <div className={monitoringOn ? '' : 'opacity-50'}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Eyebrow>Monitored employees</Eyebrow>
                    <p className="mt-1 text-[12.5px] text-ink/55 dark:text-white/55">
                      {monitoringOn
                        ? 'Of your total, how many are opted into Monitoring.'
                        : 'Turn on Monitoring to bill any employees this way.'}
                    </p>
                  </div>
                  <input
                    type="number"
                    value={monitored}
                    min={0}
                    max={employees}
                    disabled={!monitoringOn}
                    onChange={(e) => setMonitoredClamped(Number(e.target.value))}
                    aria-label="Monitored employees"
                    className="w-16 h-9 text-center rounded-full bg-white dark:bg-white/10 ring-1 ring-ink/10 dark:ring-white/15 text-ink dark:text-white text-[14px] font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:cursor-not-allowed"
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={employees}
                  value={monitored}
                  disabled={!monitoringOn}
                  onChange={(e) => setMonitoredClamped(Number(e.target.value))}
                  aria-label="Monitored employees slider"
                  className="w-full accent-coral cursor-pointer disabled:cursor-not-allowed"
                />
              </div>

              <p className="text-[11.5px] text-ink/55 dark:text-white/55 leading-relaxed">
                Payroll bills every active employee — the base fee includes the first{' '}
                {payrollIncludedSeats}, then {formatMoney(payrollSeatPrice, 'USD')} for each
                additional one.
              </p>
            </div>
          </div>

          {/* Right — sticky total card */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 rounded-3xl bg-ink-900 text-white shadow-card-lg ring-1 ring-white/10 overflow-hidden">
              <div className="p-6 lg:p-7">
                <Eyebrow className="!text-white/55">Your monthly total</Eyebrow>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-stat-xl font-bold tabular-nums leading-none">
                    {formatMoney(total, 'USD')}
                  </span>
                  <span className="text-[14px] text-white/65">/ month</span>
                </div>
                <p className="mt-2 text-[12.5px] text-white/60">
                  Free for 7 days. Card converts the trial to paid on day 8.
                </p>

                <div className="mt-6 space-y-2.5 text-[13px]">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">Core</span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(corePrice, 'USD')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">Module flats</span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(flatsTotal, 'USD')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">
                      Time &amp; Attendance · {hrBillableCount} clock-in × {formatMoney(hrSeatPrice, 'USD')}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(hrCost, 'USD')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">
                      Monitoring · {monitoredCount} monitored × {formatMoney(monitoringSeatPrice, 'USD')}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(monitoringCost, 'USD')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">
                      Payroll · {formatMoney(payrollBasePrice, 'USD')} base
                      {payrollOn && payrollBillableCount > 0
                        ? ` + ${payrollBillableCount} beyond ${payrollIncludedSeats} × ${formatMoney(payrollSeatPrice, 'USD')}`
                        : ''}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(payrollCost, 'USD')}
                    </span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="font-semibold">Total per month</span>
                    <span className="font-bold tabular-nums">{formatMoney(total, 'USD')}</span>
                  </div>
                </div>

                <Link
                  to="/signup"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
                >
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-3 text-center text-[12px] text-white/60">
                  No card required. Cancel any time.
                </p>
              </div>

              {/* Selected modules summary */}
              <div className="border-t border-white/10 px-6 lg:px-7 py-5 bg-white/[0.02]">
                <Eyebrow className="!text-white/55 mb-3">Selected modules</Eyebrow>
                <ul className="flex flex-wrap gap-1.5">
                  {activeModules.map((m) => (
                    <li
                      key={m.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-[12px] font-semibold"
                    >
                      <m.Icon className="h-3 w-3 text-coral-400" />
                      {m.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 3. MODULE BREAKDOWN TABLE
 * ───────────────────────────────────────────────────────────── */

function ModuleTable() {
  const modules = usePricedModules();
  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Modules</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            One line per module. Nothing hidden.
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl"
          >
            Monthly base for flat modules, plus a per-employee fee for the metered ones. Toggle any
            combination on or off from workspace settings.
          </motion.p>
        </motion.div>

        {/* Desktop table */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={FADE_UP}
          className="hidden md:block rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card overflow-hidden"
        >
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/8 dark:border-white/10 bg-pearl-50/80 dark:bg-white/[0.02]">
            <div className="col-span-4 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
              Module
            </div>
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55 text-right">
              Monthly base
            </div>
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55 text-right">
              Per employee
            </div>
            <div className="col-span-4 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
              What's included
            </div>
          </div>

          <ul>
            {modules.map((m, i) => {
              const free = m.base === 0 && m.seat === 0;
              return (
                <li
                  key={m.id}
                  className={
                    'grid grid-cols-12 gap-4 px-6 py-5 items-center ' +
                    (i < modules.length - 1 ? 'border-b border-ink/5 dark:border-white/5' : '')
                  }
                >
                  <div className="col-span-4 min-w-0 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shrink-0">
                      <m.Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[14.5px] font-semibold text-ink dark:text-white truncate">
                        {m.name}
                      </span>
                      {m.fixed && (
                        <span className="text-[11px] text-ink/55 dark:text-white/55">
                          Always on
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-[14px] font-bold tabular-nums text-ink dark:text-white">
                      {free
                        ? 'Free'
                        : m.base === 0
                          ? formatMoney(0, 'USD')
                          : formatMoney(m.base, 'USD')}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-[14px] font-bold tabular-nums text-coral">
                      {m.seat === 0 ? (
                        <span className="text-ink/40 dark:text-white/40">—</span>
                      ) : (
                        formatMoney(m.seat, 'USD')
                      )}
                    </span>
                    {m.seatLabel && (
                      <span className="block text-[10.5px] text-ink/45 dark:text-white/45">
                        / {m.seatLabel}
                      </span>
                    )}
                  </div>
                  <div className="col-span-4">
                    <p className="text-[13px] text-ink/65 dark:text-white/65 leading-relaxed">
                      {m.short}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-6 py-4 bg-pearl-50/80 dark:bg-white/[0.02] text-[12.5px] text-ink/55 dark:text-white/55">
            All prices in USD. Core includes email log, expenses, and payments free — Stripe's own
            processing fees still pass through on card payments.
          </div>
        </motion.div>

        {/* Mobile cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="md:hidden space-y-3"
        >
          {modules.map((m) => {
            const free = m.base === 0 && m.seat === 0;
            return (
              <motion.div
                key={m.id}
                variants={FADE_UP}
                className="rounded-2xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-5"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shrink-0">
                    <m.Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-semibold text-ink dark:text-white truncate">
                      {m.name}
                    </h3>
                    {m.fixed && (
                      <p className="text-[11px] text-ink/55 dark:text-white/55">Always on</p>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-[13px] text-ink/65 dark:text-white/65 leading-relaxed">
                  {m.short}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-pearl-50 dark:bg-white/[0.03] p-3">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                      Monthly base
                    </p>
                    <p className="mt-1 text-[15px] font-bold tabular-nums text-ink dark:text-white">
                      {free ? 'Free' : formatMoney(m.base, 'USD')}
                    </p>
                  </div>
                  <div className="rounded-xl bg-pearl-50 dark:bg-white/[0.03] p-3">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                      Per employee
                    </p>
                    <p className="mt-1 text-[15px] font-bold tabular-nums text-coral">
                      {m.seat === 0 ? '—' : formatMoney(m.seat, 'USD')}
                    </p>
                    {m.seatLabel && (
                      <p className="mt-0.5 text-[10.5px] text-ink/45 dark:text-white/45">
                        / {m.seatLabel}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 4. TRIAL MECHANICS
 * ───────────────────────────────────────────────────────────── */

function TrialSteps() {
  const steps: Array<{
    n: string;
    Icon: React.ComponentType<{ className?: string }>;
    title: string;
    body: string;
  }> = [
    {
      n: '01',
      Icon: UserPlus,
      title: 'Sign up — 7 days free',
      body: 'Email and a workspace name. No credit card. All modules enabled for the full week.',
    },
    {
      n: '02',
      Icon: Settings2,
      title: 'Pick modules + employees',
      body: 'Toggle the modules you want during the trial. Invite your team. The bill totals live.',
    },
    {
      n: '03',
      Icon: CreditCard,
      title: 'Add card to keep going',
      body: 'Add a card before day 8 to convert to paid. Skip it and the workspace goes read-only — data preserved, re-activate anytime.',
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="text-center max-w-2xl mx-auto mb-12 lg:mb-14"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Trial</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            How the free week works.
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-xl mx-auto"
          >
            Seven days, every module unlocked. We charge only after you've decided what you actually
            want and added a card.
          </motion.p>
        </motion.div>

        <motion.ol
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5"
        >
          {steps.map((s, i) => (
            <motion.li key={s.n} variants={FADE_UP} className="relative">
              <div className="h-full rounded-3xl p-7 lg:p-8 bg-white/85 dark:bg-white/[0.05] ring-1 ring-ink/5 dark:ring-white/10 shadow-card">
                <div className="flex items-center gap-3">
                  <span className="text-[44px] font-light text-coral tabular-nums tracking-tight leading-none">
                    {s.n}
                  </span>
                  <span className="hidden sm:block h-px flex-1 bg-coral/20" />
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                    <s.Icon className="h-4 w-4" />
                  </span>
                </div>
                <h3 className="mt-5 text-[20px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                  {s.title}
                </h3>
                <p className="mt-2 text-[14px] text-ink/65 dark:text-white/65 leading-relaxed">
                  {s.body}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div
                  aria-hidden
                  className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 h-7 w-7 items-center justify-center rounded-full bg-coral text-white shadow-cta"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              )}
            </motion.li>
          ))}
        </motion.ol>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          variants={FADE_UP}
          className="mt-8 max-w-3xl mx-auto rounded-2xl bg-white/70 dark:bg-white/[0.03] ring-1 ring-ink/5 dark:ring-white/10 p-5 flex items-start gap-3"
        >
          <Calendar className="h-5 w-5 text-coral shrink-0 mt-0.5" />
          <p className="text-[13.5px] text-ink/70 dark:text-white/70 leading-relaxed">
            <span className="font-semibold text-ink dark:text-white">Read-only, not deleted.</span>{' '}
            If day 8 arrives without a card, the workspace pauses and switches to read-only. Your
            data and files stay put while you decide — we only purge a workspace when you ask us
            to delete it, and then after a 30-day grace window.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 5. COMPARISON — Worqly vs. legacy stack
 * ───────────────────────────────────────────────────────────── */

function Comparison() {
  const modules = usePricedModules();
  const byId = React.useMemo(() => {
    const m = new Map<ModuleId, ModuleDef>();
    modules.forEach((mod) => m.set(mod.id, mod));
    return m;
  }, [modules]);

  const legacy: Array<{ tool: string; covers: string; price: string }> = [
    { tool: 'QuickBooks',  covers: 'Invoicing + bookkeeping',    price: '$60 / mo' },
    { tool: 'Gusto',       covers: 'HR + payroll (4 people)',    price: '$80 / mo' },
    { tool: 'Toggl Track', covers: 'Time tracking',              price: '$36 / mo' },
    { tool: 'Expensify',   covers: 'Expense management',         price: '$40 / mo' },
  ];
  const legacyTotal = '$216 / mo';

  // Worqly equivalent scope: Core + Invoicing + Time & Attendance + Payroll +
  // Projects, 4 employees, no one monitored. Expenses is included free with
  // Core (no separate module charge). Computed with the same helper the
  // calculator uses — never a hardcoded number.
  const worqlyEmployees = 4;
  const worqlyToggles = {
    invoicingOn: true, projectsOn: true, esignOn: false,
    hrOn: true, monitoringOn: false, payrollOn: true,
  };
  const worqlyBreakdown = computeMonthlyTotal({
    corePrice: byId.get('core')?.base ?? FALLBACK_PRICES.core,
    invoicingPrice: byId.get('invoicing')?.base ?? FALLBACK_PRICES.invoicing,
    projectsPrice: byId.get('projects')?.base ?? FALLBACK_PRICES.projects,
    esignPrice: byId.get('esign')?.base ?? FALLBACK_PRICES.esign,
    hrSeatPrice: byId.get('hr')?.seat ?? FALLBACK_PRICES.hr,
    monitoringSeatPrice: byId.get('monitoring')?.seat ?? FALLBACK_PRICES.monitoring,
    payrollBasePrice: byId.get('payroll')?.base ?? FALLBACK_SEAT_BASES.payroll,
    payrollSeatPrice: byId.get('payroll')?.seat ?? FALLBACK_PRICES.payroll,
    payrollIncluded: byId.get('payroll')?.included ?? FALLBACK_INCLUDED_SEATS.payroll,
    ...worqlyToggles,
    employees: worqlyEmployees, monitored: 0,
  });
  const worqlyTotal = worqlyBreakdown.total;
  // Label reflects the actual toggled-on module count above, never a hardcoded number.
  const worqlyModuleCount = Object.values(worqlyToggles).filter(Boolean).length;

  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Worth it?</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Or you can keep stitching four tools together.
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl"
          >
            A small operating team running QuickBooks + Gusto + Toggl + Expensify is paying $200+ /
            month — for four logins, four invoices, and zero integration between them.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          variants={FADE_UP}
          className="grid grid-cols-1 lg:grid-cols-2 gap-5"
        >
          {/* Legacy stack */}
          <div className="rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-7 lg:p-8">
            <div className="flex items-center justify-between mb-5">
              <Eyebrow>The legacy stack</Eyebrow>
              <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] text-ink/45 dark:text-white/45">
                4 tools
              </span>
            </div>
            <ul className="space-y-2.5">
              {legacy.map((row) => (
                <li
                  key={row.tool}
                  className="flex items-center justify-between gap-3 rounded-2xl bg-pearl-50 dark:bg-white/[0.03] px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-ink dark:text-white truncate">
                      {row.tool}
                    </p>
                    <p className="text-[12px] text-ink/55 dark:text-white/55 truncate">
                      {row.covers}
                    </p>
                  </div>
                  <span className="text-[13.5px] font-bold tabular-nums text-ink/65 dark:text-white/65 shrink-0">
                    {row.price}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-ink/8 dark:border-white/10 flex items-baseline justify-between">
              <span className="text-[13px] text-ink/55 dark:text-white/55">Combined total</span>
              <span className="text-[28px] font-bold tabular-nums text-ink dark:text-white">
                {legacyTotal}
              </span>
            </div>
            <p className="mt-3 text-[12.5px] text-ink/55 dark:text-white/55">
              Public list prices, smallest paid tier per tool, billed monthly.
            </p>
          </div>

          {/* Worqly */}
          <div className="relative rounded-3xl bg-coral-gradient text-white shadow-card-lg ring-1 ring-white/10 overflow-hidden p-7 lg:p-8">
            <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
            <div className="relative">
              <div className="flex items-center justify-between mb-5">
                <Eyebrow className="!text-white/80">All on Worqly</Eyebrow>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] text-white/70">
                  1 bill
                </span>
              </div>
              <ul className="space-y-2.5">
                {[
                  { label: 'Invoicing',           covers: 'Branded PDFs + Stripe pay links' },
                  { label: 'Time & Attendance',   covers: 'Clock-in, attendance, leave' },
                  { label: 'Payroll',             covers: 'Runs, payslips, bank export' },
                  { label: 'Projects + Time',     covers: 'Tasks, hours, margin' },
                  { label: 'Expenses',            covers: 'Included free with Core' },
                ].map((row) => (
                  <li
                    key={row.label}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-white/15 backdrop-blur-md px-4 py-3"
                  >
                    <div className="min-w-0 flex items-center gap-2.5">
                      <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold truncate">{row.label}</p>
                        <p className="text-[12px] text-white/75 truncate">{row.covers}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-white/20 flex items-baseline justify-between">
                <span className="text-[13px] text-white/75">Core + {worqlyModuleCount} modules · {worqlyEmployees} employees</span>
                <span className="text-[28px] font-bold tabular-nums">
                  {formatMoney(worqlyTotal, 'USD')}{' '}
                  <span className="text-[14px] font-medium text-white/75">/ mo</span>
                </span>
              </div>
              <p className="mt-3 text-[12.5px] text-white/80">
                Same scope, one login, one invoice. Plus 7 days free to try it.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 6. FAQ
 * ───────────────────────────────────────────────────────────── */

function FAQ() {
  const items: Array<{ q: string; a: string }> = [
    {
      q: 'What\'s a clock-in employee and a monitored employee? How is Payroll billed?',
      a: 'Time & Attendance ($2/mo) bills every active employee who clocks in — a clock-in employee. Monitoring ($7/mo) bills only the employees you\'ve opted into the desktop agent — a monitored employee. An employee is billed clock-in OR monitored, never both: if someone is monitored, that $7 replaces their $2 clock-in charge instead of adding to it. Payroll is different — it\'s not tied to a special employee definition. It\'s a flat $15/mo that includes your first 10 active employees, then $1.50/mo for each additional one, and stacks independently on top of everything else.',
    },
    {
      q: 'What if I don\'t monitor anyone?',
      a: 'Then Monitoring stays off and every active employee on Time & Attendance bills as a clock-in employee at $2/mo. Turn Monitoring on for specific people later and their $7 monitored rate simply replaces their $2 clock-in charge — you\'re never billed both for the same person.',
    },
    {
      q: 'Can I remove a module mid-month?',
      a: 'Yes. Turning a module off stops it working immediately, and the module returns to a read-only state so your data is preserved if you turn it back on later. Because billing is in advance, removing a flat-priced module (Invoicing, Projects or E-Sign) mid-cycle credits you a prorated amount for the remaining days on your next invoice. The per-employee modules — Time & Attendance, Monitoring and Payroll — aren\'t prorated: turning one off stops it from your next charge, but the cycle you\'ve already paid for isn\'t credited back.',
    },
    {
      q: 'When am I charged?',
      a: 'In advance. On your renewal date we charge your card for the month ahead, based on your workspace as it stands that day. Adding or removing a flat-priced module (Invoicing, Projects, E-Sign) mid-month is prorated for the remaining days; the per-employee modules aren\'t prorated. Employee count is taken on the charge date — hire someone mid-month and they\'re included from your next charge, at no extra cost for the current one. The reverse is also true: deactivate someone mid-month and they still count toward the charge already issued — there\'s no partial refund, they simply drop off your next charge.',
    },
    {
      q: 'What if I delete the company before the trial ends?',
      a: 'Nothing is charged. The 7-day trial requires no card, so if you delete the workspace before day 8 (or simply never add a card) there\'s no payment and no further obligation. A deleted workspace sits in a 30-day grace window (restorable if you change your mind) and is then purged.',
    },
    {
      q: 'Do I need a card to start?',
      a: 'No. Sign up with an email and a workspace name and you\'re inside. We don\'t ask for a card until you decide to convert the trial to paid — usually some time before day 8.',
    },
    {
      q: 'Can I downgrade?',
      a: 'Anytime. Turn off a flat-priced module and the remaining days of the cycle you\'ve prepaid are credited on your next invoice; turn off a per-employee module and it simply drops off your next charge. There\'s no exit fee and no annual lock-in.',
    },
    {
      q: 'Is there an annual discount?',
      a: 'Not yet. We\'re a young product and we\'d rather earn the renewal each month than lock you in for a year. Annual plans with a discount will land once we\'ve heard enough customers ask for them.',
    },
  ];

  const [open, setOpen] = React.useState<number | null>(0);
  const faqSchema = React.useMemo(() => buildFaqPageSchema(items), [items]);

  return (
    <section id="faq" className="py-20 lg:py-24">
      <JsonLd id="faq" schema={faqSchema} />
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="text-center mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Pricing FAQ</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Eight straight answers about the bill.
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="space-y-3"
        >
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={it.q}
                variants={FADE_UP}
                className="rounded-2xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left focus-coral rounded-2xl"
                >
                  <span className="text-[15px] font-semibold text-ink dark:text-white">{it.q}</span>
                  <ChevronDown
                    className={
                      'h-4 w-4 shrink-0 transition-transform text-ink/55 dark:text-white/55 ' +
                      (isOpen ? 'rotate-180' : '')
                    }
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-[14px] text-ink/70 dark:text-white/70 leading-relaxed">
                      {it.a}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 7. FOOTER CTA — email → /signup?email=
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    const qs = params.toString();
    window.location.href = qs ? `/signup?${qs}` : '/signup';
  }

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                Start free
              </p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Start free in 60 seconds.
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start. Pick the modules you actually need — pay only
                for the people who use them.
              </p>
            </div>

            <div className="lg:col-span-5">
              <form
                onSubmit={onSubmit}
                className="rounded-2xl bg-white/15 backdrop-blur-md ring-1 ring-white/25 p-2 flex flex-col sm:flex-row sm:items-center gap-2"
              >
                <label htmlFor="pricing-cta-email" className="sr-only">
                  Work email
                </label>
                <input
                  id="pricing-cta-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 min-w-0 w-full h-11 px-4 rounded-full bg-white text-ink text-[14px] placeholder:text-ink/40 outline-none focus:ring-2 focus:ring-white/60"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex shrink-0 w-full sm:w-auto items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-ink-900 text-white text-[13.5px] font-semibold hover:bg-black transition-colors disabled:opacity-60"
                >
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              <p className="mt-3 text-[12px] text-white/75 text-center sm:text-left">
                No card required. Add billing before day 8 to keep going.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
